import Head from 'next/head'
import { useTranslation } from '@i18n'
import { TagArticles_tagArticles_articles } from '@apollo-types/TagArticles'
import { tagUrl, publicUrl } from '@libs/appUrl'

type TagHeadProps = {
  publishedAt?: string
  modifiedAt?: string
  tag?: string
  currentPage?: number
  articles?: TagArticles_tagArticles_articles[]
}

function TagHead(props: TagHeadProps) {
  const { currentPage, tag, articles = [], publishedAt, modifiedAt } = props
  const firstArticle = articles?.[0]

  const { t } = useTranslation('tag')

  const makeTitle = () => {
    return currentPage > 1
      ? t('metaTitleWithPage', { tag, page: currentPage })
      : t('metaTitle', { tag })
  }

  const makeDescription = () => {
    const sliceArticles = articles.slice(0, 3)
    return sliceArticles.map((article) => article?.metaTitle ?? '').join('、')
  }

  const getCoverImageFromFirstArticle = () => {
    const coverImage = firstArticle?.coverImage?.croppedUrl
    return coverImage
  }

  const webPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: makeTitle(),
    description: makeDescription(),
    keywords: firstArticle?.tags,
    dateCreated: publishedAt,
    datePublished: publishedAt,
    dateModified: modifiedAt,
    primaryImageOfPage: {
      '@type': 'ImageObject',
      contentUrl: getCoverImageFromFirstArticle() || publicUrl('/images/popdaily.png'),
    },
  }

  const itemList = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: [
      (articles.slice(0, 10) || []).map((article, index) => ({
        '@type': 'ListItem',
        name: article?.title ?? '',
        image: article?.coverImage?.croppedUrl ?? '',
        url: article?.url ?? '',
        position: index + 1,
      })),
    ],
  }

  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'PopDaily 波波黛莉的異想世界',
        item: publicUrl(),
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: tag,
        item: tagUrl(tag),
      },
    ],
  }

  const webSite = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: 'https://www.popdaily.com.tw/',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.popdaily.com.tw/tag/{tag_string}',
      'query-input': 'required name=tag_string',
    },
  }

  return (
    <Head>
      <title key="title">{makeTitle()}</title>
      <meta key="description" name="description" content={makeDescription()} />
      {/* canonical link 一律設第一頁的路徑 */}
      <link key="canonical" rel="canonical" href={tagUrl(tag)} />
      <link
        type="application/rss+xml"
        rel="alternate"
        title={makeTitle()}
        href="/api/v2/search/rss"
      />
      {/* no index 沒有標籤結果的頁面，以及第二頁與之後的頁面 */}
      {(articles.length === 0 || currentPage > 1) && (
        <meta key="robots" name="robots" content="noindex, nofollow" />
      )}

      {/* WebPage */}
      <script
        key="WebPage"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPage) }}
      />
      {/* ItemList */}
      {articles.length > 0 && (
        <script
          key="ItemList"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(itemList) }}
        />
      )}
      {/* BreadcrumbList */}
      <script
        key="BreadcrumbList"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbList) }}
      />
      {/* WebSite */}
      <script
        key="WebSite"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webSite) }}
      />
    </Head>
  )
}

export default TagHead
